/**
 * A film's soundtrack, played under the page's canvas.
 *
 * Off until the viewer turns it on — a page that starts making noise on its own is a page
 * people close — and the turning on is a click, which is the only thing that lets a
 * browser start an `AudioContext` at all. The soundtrack is rendered once, whole
 * (`renderSoundtrack`), and played from wherever the film is: a seek restarts it there,
 * a pause stops it, and while it plays the player follows its clock (`Player.follow`), so
 * a knock and the frame that shows it stay together however the frames are paced.
 */

import type { Film, Site } from "./film.js";
import type { Player } from "./player.js";
import { renderSoundtrack } from "./sound.js";

/** How far the picture may drift from the sound before the sound is moved to it. */
const SLACK = 0.15;

export class Speaker {
  private ctx: AudioContext | undefined;
  private soundtrack: Promise<AudioBuffer | undefined> | undefined;
  private playing: { source: AudioBufferSourceNode; level: GainNode; startedAt: number; from: number } | undefined;
  private on = false;
  private readonly unsubscribe: () => void;

  constructor(
    private readonly player: Player,
    private readonly film: Film,
    private readonly site: Site,
  ) {
    this.unsubscribe = player.subscribe(() => this.sync());
  }

  get enabled(): boolean {
    return this.on;
  }

  /** Turns the sound on. Call it from the click that asked for it, before anything awaits. */
  async enable(): Promise<void> {
    this.ctx ??= new AudioContext({ latencyHint: "playback" });
    const resumed = this.ctx.resume();
    this.soundtrack ??= renderSoundtrack(this.film, this.site);
    await resumed;
    if ((await this.soundtrack) === undefined) {
      return;
    }
    this.on = true;
    this.player.follow(() => this.heard());
    this.sync();
  }

  disable(): void {
    this.on = false;
    this.player.follow(undefined);
    this.stop(0.04);
  }

  dispose(): void {
    this.unsubscribe();
    this.disable();
    void this.ctx?.close();
  }

  /** Where in the film the sound is, as the viewer hears it, or `undefined` when it is silent. */
  private heard(): number | undefined {
    const { ctx, playing } = this;
    if (ctx === undefined || playing === undefined) {
      return undefined;
    }
    const latency = ctx.outputLatency || ctx.baseLatency || 0;
    return playing.from + Math.max(0, ctx.currentTime - playing.startedAt - latency);
  }

  private sync(): void {
    if (!this.on) {
      return;
    }
    const { player } = this;
    if (!player.isPlaying) {
      // A film that ran to its end lets its last chord ring; a pause stops it.
      if (!player.ended) {
        this.stop(0.04);
      }
      return;
    }
    const heard = this.heard();
    if (heard === undefined || Math.abs(heard - player.time) > SLACK) {
      void this.start(player.time);
    }
  }

  private async start(from: number): Promise<void> {
    const ctx = this.ctx;
    const buffer = await this.soundtrack;
    if (ctx === undefined || buffer === undefined || !this.on) {
      return;
    }
    this.stop(0.02);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const level = ctx.createGain();
    source.connect(level);
    level.connect(ctx.destination);
    const startedAt = ctx.currentTime + 0.03;
    source.start(startedAt, Math.min(from, buffer.duration));
    const playing = { source, level, startedAt, from };
    source.onended = () => {
      if (this.playing === playing) {
        this.playing = undefined;
      }
    };
    this.playing = playing;
  }

  /** Stops what is playing with a short fade, so the cut is not a click. */
  private stop(fade: number): void {
    const { ctx, playing } = this;
    if (ctx === undefined || playing === undefined) {
      return;
    }
    this.playing = undefined;
    const now = ctx.currentTime;
    playing.level.gain.setValueAtTime(playing.level.gain.value, now);
    playing.level.gain.linearRampToValueAtTime(0, now + fade);
    playing.source.stop(now + fade + 0.01);
  }
}
